import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fmt, fmtDate } from "../Dashboard/DashboardPrimitives";
import TablePagination from "src/components/TablePagination/TablePagination";
import { useClientPagination } from "src/hooks/useClientPagination";
import type { SumupPayment } from "src/hooks/useSumup";

/** Matches the Bank page's own search box: invoice number, payer/patient, SumUp transaction code. */
export function cardPaymentMatchesSearch(p: SumupPayment, q: string): boolean {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [p.readableId, p.recipientName, p.patientName, p.transactionCode]
        .some((v) => (v ?? "").toLowerCase().includes(s));
}

/**
 * Card payments patients made in the portal via SumUp (chapter 10). Each one is already booked on
 * its invoice; this list is for reconciling SumUp's batched payouts and finding a payment in the
 * SumUp dashboard by its transaction code.
 */
export function CardPaymentsTable({ payments, accentColor, showAccount }: {
    payments: SumupPayment[];
    accentColor: string;
    showAccount: boolean;
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { page, setPage, pageSize, setPageSize, totalPages, pagedItems, totalCount, PAGE_SIZES } =
        useClientPagination(payments, [payments.length]);

    return (
        <>
            <p className="bank-card-payments__hint">{t("sumup.paymentsHint")}</p>
            {totalCount > 0 && (
                <TablePagination
                    placement="roster-top"
                    loading={false}
                    accentColor={accentColor}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSizes={PAGE_SIZES}
                />
            )}
            <div className="table-container">
                <table className="dashboard-table bank-table">
                    <thead>
                        <tr>
                            <th>{t("finapi.colDate")}</th>
                            {showAccount && <th>{t("sumup.colAccount")}</th>}
                            <th>{t("finapi.colAmount")}</th>
                            <th>{t("sumup.colInvoice")}</th>
                            <th>{t("sumup.colPayer")}</th>
                            <th>{t("sumup.colTransaction")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagedItems.length === 0 && (
                            <tr><td colSpan={showAccount ? 6 : 5} className="finapi-table__empty">{t("sumup.noPayments")}</td></tr>
                        )}
                        {pagedItems.map((p) => (
                            <tr key={p.id} className="clickable-row finapi-table__row" onClick={() => navigate(`/invoices/${p.invoiceId}`)}>
                                <td className="finapi-table__date">{fmtDate(p.paidAt)}</td>
                                {showAccount && (
                                    <td className="finapi-table__owner">
                                        {p.account === "praxis" ? t("sumup.praxisAccountShort") : p.account ?? "—"}
                                    </td>
                                )}
                                <td><span className="finapi-table__amt finapi-table__amt--in">+ {fmt(p.amount)}</span></td>
                                <td>{p.readableId ?? "—"}</td>
                                <td className="finapi-table__counterpart">
                                    {p.recipientName ?? "—"}
                                    {p.patientName && p.patientName !== p.recipientName && (
                                        <div className="finapi-table__matched-info"><span>{p.patientName}</span></div>
                                    )}
                                </td>
                                <td className="finapi-table__purpose">{p.transactionCode ?? "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
